import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LogarithmicScale,
  Tooltip,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { Footprint } from '../../models/footprint'
import { formatNumber } from '../../utils/formatNumber'

ChartJS.register(CategoryScale, LogarithmicScale, BarElement, Tooltip)
export const options = {
  indexAxis: 'y' as const,
  elements: {
    bar: {
      borderWidth: 1,
    },
  },
  plugins: {
    tooltip: {
      enabled: false,
    },
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      ticks: {
        position: 'left' as const,
        crossAlign: 'far',
        color: 'black',
        font: {
          size: 14,
          family: 'sans-serif',
        },
        callback: function (value: number, index: number): string {
          // Get the full label from the chart data
          const labels = (this as any).chart.data.labels as string[]
          if (!labels || !labels[index]) return ''
          const category = labels[index].split(':')[0]
          return category.length > 25
            ? category.substring(0, 17) + '...'
            : category
        },
        maxRotation: 0,
        padding: 5,
        align: 'center',
      },
      grid: {
        display: false,
      },
      afterFit: function (scale: any) {
        // Ensure minimum width for the axis
        scale.width = Math.max(scale.width, 150)
      },
    },
    y1: {
      position: 'right' as const,
      ticks: {
        crossAlign: 'far',
        color: 'black',
        font: {
          size: 14,
          family: 'sans-serif',
        },
        callback: function (value: number, index: number): string {
          // Get the full label from the chart data
          const labels = (this as any).chart.data.labels as string[]
          if (!labels || !labels[index]) return ''
          const label = labels[index]
          const percentMatch = label.match(/: (\d+)%/)
          const valueMatch = label.match(/\(([\d,.]+) t\)/)
          if (!percentMatch || !valueMatch) return ''
          return `${percentMatch[1]}% (${valueMatch[1]} t)`
        },
      },
      grid: {
        display: false,
      },
    },
    x: {
      // type: 'logarithmic' as any,
      display: false,
      ticks: {
        display: false,
      },
      grid: {
        display: false,
      },
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  barThickness: 30,
}

export interface FootprintChartProps {
  footprint: Footprint
}

export const FootprintChart = ({ footprint }: FootprintChartProps) => {
  const { t } = useTranslation(['translation', 'modifierSurvey'])
  const { total: _, ...rest } = footprint

  const chartData = {
    fuels: Number.parseInt(rest.fuels) / 1000,
    businessTravel: Number.parseInt(rest.businessTravel) / 1000,
    employeeCommuting: Number.parseInt(rest.employeeCommuting) / 1000,
    upstreamLeasedAssets: Number.parseInt(rest.upstreamLeasedAssets) / 1000,
    heatCoolingSteam: Number.parseInt(rest.heatCoolingSteam) / 1000,
    fuelEnergyRelatedActivities:
      Number.parseInt(rest.fuelEnergyRelatedActivities) / 1000,
    upstreamTransportation: Number.parseInt(rest.upstreamTransportation) / 1000,
    processFugitiveEmissions:
      Number.parseInt(rest.processFugitiveEmissions) / 1000,
    heatingCombustionOnsite:
      Number.parseInt(rest.heatingCombustionOnsite) / 1000,
    capitalGoods: Number.parseInt(rest.capitalGoods) / 1000,
    purchasedGoodsServices: Number.parseInt(rest.purchasedGoodsServices) / 1000,
    electricity: Number.parseInt(rest.electricity) / 1000,
    waste: Number.parseInt(rest.waste) / 1000,
  }

  // Calculate total for percentage
  const totalValue = Object.values(chartData).reduce(
    (sum, value) => sum + value,
    0
  )

  // Create and sort the data entries
  const sortedEntries = Object.entries(chartData)
    .map(([key, value]) => ({
      key,
      value,
      label: `${t(`modifierSurvey:chart.${key}`)}: ${Math.round(
        (value / totalValue) * 100
      )}% (${formatNumber(value)} t)`,
    }))
    .sort((a, b) => b.value - a.value)

  const data = {
    labels: sortedEntries.map((entry) => entry.label),
    datasets: [
      {
        data: sortedEntries.map((entry) => entry.value),
        backgroundColor: Array(sortedEntries.length).fill(
          'rgba(59, 130, 246, 0.5)'
        ),
        borderRadius: 8,
        yAxisID: 'y',
      },
    ],
  }

  return (
    <>
      <dt className="truncate text-sm font-medium text-gray-500 flex items-center">
        {t('dashboard.charts.footprintByCategory')}
      </dt>
      <dd className="mt-4 text-3xl font-semibold tracking-tight text-gray-900">
        <div className="min-h-[30rem]">
          <Bar options={options as any} data={data} />
        </div>
      </dd>
    </>
  )
}
